import { useCallback, useEffect, useMemo } from 'react';
import {
	HierarchyNode,
	GetHierarchyParams,
	CompleteHierarchyResponse,
} from 'core/API/hierarchy';
import useSettingsStore from 'store/useSettingsStore';

interface UseHierarchyReturn {
	hierarchyData: CompleteHierarchyResponse | null;
	loading: boolean;
	error: string | null;
	refetch: () => void;
	buildHierarchyTree: () => HierarchyTreeNode[];
}

export interface HierarchyTreeNode extends HierarchyNode {
	children: HierarchyTreeNode[];
	parent?: HierarchyTreeNode;
}

/**
 * Fetches the complete organisational hierarchy from the shared settings store.
 * Skips the network call when hierarchy is already cached unless `refetch` is used.
 */
export const useHierarchy = (params: GetHierarchyParams = {}): UseHierarchyReturn => {
	const hierarchyData = useSettingsStore((s) => s.completeHierarchy);
	const loading = useSettingsStore((s) => s.completeHierarchyLoading);
	const error = useSettingsStore((s) => s.completeHierarchyError);
	const fetchCompleteHierarchy = useSettingsStore((s) => s.fetchCompleteHierarchy);

	const memoizedParams = useMemo(
		() => ({
			page: params.page || 1,
			itemsPerPage: params.itemsPerPage || 50,
			inFocus: params.inFocus || null,
		}),
		[params.page, params.itemsPerPage, params.inFocus]
	);

	const buildHierarchyTree = useCallback((): HierarchyTreeNode[] => {
		if (!hierarchyData || hierarchyData.combinedHierarchy.length === 0) return [];

		const nodes = hierarchyData.combinedHierarchy;
		const nodeMap = new Map<number, HierarchyTreeNode>();
		const rootNodes: HierarchyTreeNode[] = [];

		nodes.forEach((node) => {
			nodeMap.set(node.id, {
				...node,
				children: [],
			});
		});

		const processedNodes = new Set<number>();

		nodes.forEach((node) => {
			const currentNode = nodeMap.get(node.id);
			if (!currentNode || processedNodes.has(node.id)) return;

			let parentId = node.parentId;
			const visited = new Set<number>();

			while (parentId !== null && nodeMap.has(parentId)) {
				if (visited.has(parentId)) {
					parentId = null;
					break;
				}
				visited.add(parentId);
				const parent = nodes.find((n) => n.id === parentId);
				parentId = parent ? parent.parentId : null;
			}

			if (node.parentId === null || !nodeMap.has(node.parentId)) {
				rootNodes.push(currentNode);
			} else {
				const parentNode = nodeMap.get(node.parentId);
				if (parentNode) {
					currentNode.parent = parentNode;
					parentNode.children.push(currentNode);
				}
			}

			processedNodes.add(node.id);
		});

		const typePriority = { organization: 0, group: 1, workplace: 2 };

		const sortTree = (tree: HierarchyTreeNode[]): HierarchyTreeNode[] => {
			return tree
				.sort((a, b) => {
					const aPriority = typePriority[a.type] ?? 3;
					const bPriority = typePriority[b.type] ?? 3;
					if (aPriority !== bPriority) return aPriority - bPriority;
					return a.name.localeCompare(b.name);
				})
				.map((node) => ({
					...node,
					children: sortTree(node.children),
				}));
		};

		return sortTree(rootNodes);
	}, [hierarchyData]);

	const refetch = useCallback(() => {
		fetchCompleteHierarchy(memoizedParams, { force: true });
	}, [fetchCompleteHierarchy, memoizedParams]);

	useEffect(() => {
		fetchCompleteHierarchy(memoizedParams);
	}, [fetchCompleteHierarchy, memoizedParams]);

	return {
		hierarchyData,
		loading,
		error,
		refetch,
		buildHierarchyTree,
	};
};
